import React, { useEffect, useState } from 'react'
import { Link, navigate } from 'gatsby'
import { useLocation } from '@reach/router'
import { useAuthRevisor } from '../../hooks/useAuth'
import './style.less'
import {
    HomeOutlined,
    SettingOutlined,
    UserOutlined,
    LockOutlined,
    ExclamationCircleTwoTone,
    BookOutlined,
    FileSyncOutlined,
    FolderOutlined,
    FileTextOutlined,
    LoadingOutlined,
    SyncOutlined,
} from '@ant-design/icons'
import { Layout, Menu, message, Spin } from 'antd'
import RevisorSettings from './Settings'
import { httpGetRevisor, httpPostRevisor } from '../../services/http'
import RevisorOverview from './Overview'
import { ClientOverview, getClientsRes } from './types'
import SubMenu from 'antd/lib/menu/SubMenu'
import { ITaxYear, ITaxYearDataRes } from '../ComplianceReports/types'
import { User } from '../../types/session'
import makeStyles from '@mui/styles/makeStyles';
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'

const { Sider } = Layout

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        maxWidth: 360,
        backgroundColor: theme.palette.background.paper,
    },
    dividerFullWidth: {
        margin: `5px 0 0 ${theme.spacing(2)}`,
        color: '#D7E6F5',
    },
    dividerCol: {
        backgroundColor: '#D7E6F5',
    },
}))

export default function RevisorNav({
    getTaxYearData,
    loadOverviewData,
    renderTaxYearContent,
    selectedKey,
    setSelectedKey,
    taxYears,
    isLoading,
    userData,
    isBackgroundLoading,
}: {
    getTaxYearData: (taxYear: ITaxYear, forceFetch?: boolean) => Promise<ITaxYearDataRes>
    loadOverviewData: (postSwitchToTab?: string) => Promise<ITaxYear[]>
    renderTaxYearContent: any
    selectedKey: string
    setSelectedKey: (v: string) => void
    taxYears: ITaxYear[]
    isLoading: boolean
    userData: User | null
    isBackgroundLoading: boolean
}) {
    let location = useLocation()
    const [userName, setUserName] = useState<string>('')
    const [clients, setClients] = useState<ClientOverview[]>([])
    const [advisorAccessId, setAdvisorAccessId] = useState<string>(null)
    const classes = useStyles()

    useEffect(() => {
        const sessionId = new URLSearchParams(location.search).get('session_id')
        const advisorAccessIdFromQuery = new URLSearchParams(location.search).get(
            'advisor_access_id'
        )
        if (!advisorAccessIdFromQuery || advisorAccessId || !sessionId) return
        setAdvisorAccessId(advisorAccessIdFromQuery)
        setSelectedKey('loading')
        httpPostRevisor<Response>(
            `/auth-revisor/tax-year-complete-checkout/${advisorAccessIdFromQuery}/${sessionId}`
        )
            .then((r) => {
                loadOverviewData().then((newTaxYears) => {
                    const ty = newTaxYears.find(
                        (ty) => ty.advisor_access_id === advisorAccessIdFromQuery
                    )
                    setSelectedKey(`taxYear;;;${ty.id}`)
                    message.success(
                        `Tax Year ${ty.tax_year} added to client ${ty.ll_user_full_name}`
                    )
                })
                httpGetRevisor<getClientsRes>('/advisors/').then((res) => {
                    setClients(res.data.clients)
                })
                const queryParams = new URLSearchParams(location.search)
                queryParams.delete('advisor_access_id')
                queryParams.delete('session_id')
                navigate('/revisor', { replace: true })
            })
            .catch((error) => {
                message.error(error.toString())
                setSelectedKey('overview')
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, loadOverviewData, setSelectedKey])

    useEffect(() => {
        httpGetRevisor<getClientsRes>('/advisors/')
            .then((res) => {
                setClients(res.data.clients)
            })
            .catch(() => {})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const { session } = useAuthRevisor('')

    useEffect(() => {
        if (!(session && session.user && session.user.name)) return
        setUserName(session.user.name)
    }, [session])

    const LoadingComp = <SyncOutlined style={{ fontSize: 100 }} spin />

    function renderContent() {
        switch (selectedKey) {
            case 'create':
                return (
                    <RevisorOverview
                        loadOverviewData={loadOverviewData}
                        session={session}
                        clients={clients}
                        setClients={setClients}
                        setSelectedKey={setSelectedKey}
                    />
                )
            case 'settings':
                return <RevisorSettings session={session} />
            case 'loading':
                return (
                    <Spin spinning={true} indicator={LoadingComp} className="report-spinner">
                        <div className="report-container" />
                    </Spin>
                )
            default:
                return renderTaxYearContent()
        }
    }

    return (
        <>
            <div style={{ height: '56px' }}>
                <div className="nav-header-reporting">
                    <div className="header-content-reporting">
                        <Link to="/">
                            <div style={{ marginRight: 20 }} className="logoImg" />
                        </Link>
                        <h3 className="header-title-reporting">{userName}</h3>
                        {isBackgroundLoading && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    textAlign: 'right',
                                    marginLeft: 20,
                                }}
                            >
                                <h4 className="inverse-h" style={{ marginRight: '10px' }}>
                                    Updating...
                                </h4>
                                <SyncOutlined
                                    style={{ fontSize: 24, color: 'white', alignSelf: 'center' }}
                                    spin
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Layout>
                <Sider width={300} className="side-bar-reporting">
                    <Menu theme="dark" mode="inline" selectedKeys={[selectedKey]}>
                        <Menu.Item
                            key="create"
                            icon={<HomeOutlined />}
                            onClick={() => {
                                setSelectedKey('create')
                            }}
                        >
                            Home
                        </Menu.Item>
                        <Menu.Item
                            key="settings"
                            icon={<SettingOutlined />}
                            onClick={() => {
                                setSelectedKey('settings')
                            }}
                        >
                            Settings
                        </Menu.Item>
                        {/* <Divider style={{ color: '#D7E6F5' }} plain orientation="center">
                            Clients
                        </Divider> */}
                        <Divider component="li" light classes={{light: classes.dividerCol}} />
                        <li>
                            <Typography
                                className={classes.dividerFullWidth}
                                color="textSecondary"
                                display="block"
                                variant="caption"
                            >
                                Clients
                            </Typography>
                        </li>
                        {clients
                            .sort((a, b) =>
                                a.client_info.full_name.localeCompare(b.client_info.full_name)
                            )
                            .map((c) => {
                                return (
                                    <SubMenu
                                        key={`clientSubMenu;;;${c.client_info.ll_user_id}`}
                                        icon={<UserOutlined />}
                                        title={c.client_info.full_name}
                                    >
                                        {c.tax_years
                                            .filter((cty) => cty.is_accepted)
                                            .sort((a, b) => Number(a.tax_year) - Number(b.tax_year))
                                            .map((cty, idx) => {
                                                const ty = taxYears.find(
                                                    (t) => t.id === cty.ll_tax_year_id
                                                )
                                                if (!ty)
                                                    return (
                                                        <Menu.Item key={`dummy-${idx}`}>
                                                            <LoadingOutlined />
                                                        </Menu.Item>
                                                    )
                                                return (
                                                    <>
                                                        <SubMenu
                                                            key={`taxYearSubMenu;;;${ty.id}`}
                                                            icon={
                                                                ty.is_locked ? (
                                                                    <LockOutlined />
                                                                ) : (
                                                                    <FileSyncOutlined />
                                                                )
                                                            }
                                                            title={
                                                                <span>
                                                                    {`${ty.tax_year}`}
                                                                    {!ty.is_updated && (
                                                                        <ExclamationCircleTwoTone twoToneColor="#f5222d" />
                                                                    )}
                                                                </span>
                                                            }
                                                        >
                                                            <Menu.Item
                                                                key={`taxYear;;;${ty.id}`}
                                                                icon={<BookOutlined />}
                                                                onClick={() => {
                                                                    setSelectedKey(
                                                                        `taxYear;;;${ty.id}`
                                                                    )
                                                                }}
                                                            >
                                                                <span>
                                                                    Overview{' '}
                                                                    {!ty.is_updated && (
                                                                        <ExclamationCircleTwoTone twoToneColor="#f5222d" />
                                                                    )}
                                                                </span>
                                                            </Menu.Item>
                                                            <SubMenu
                                                                key={`taxYearReportMenu;;;${ty.id}`}
                                                                icon={<FolderOutlined />}
                                                                title="Compliance Reports"
                                                            >
                                                                {ty.report_ids
                                                                    .sort(
                                                                        (a, b) =>
                                                                            Number(a.idx) -
                                                                            Number(b.idx)
                                                                    )
                                                                    .map((reportIdObj) => {
                                                                        return (
                                                                            <Menu.Item
                                                                                key={`taxYearReport;;;${ty.id}:::${reportIdObj.id}`}
                                                                                icon={
                                                                                    reportIdObj.is_locked ? (
                                                                                        <LockOutlined />
                                                                                    ) : (
                                                                                        <FileTextOutlined />
                                                                                    )
                                                                                }
                                                                                onClick={() => {
                                                                                    setSelectedKey(
                                                                                        `taxYearReport;;;${ty.id}:::${reportIdObj.id}`
                                                                                    )
                                                                                }}
                                                                            >
                                                                                Report -{' '}
                                                                                {reportIdObj.idx}
                                                                            </Menu.Item>
                                                                        )
                                                                    })}
                                                            </SubMenu>
                                                        </SubMenu>
                                                    </>
                                                )
                                            })}
                                    </SubMenu>
                                )
                            })}
                    </Menu>
                </Sider>
                <Layout>{renderContent()}</Layout>
            </Layout>
        </>
    )
}
